import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, User } from "lucide-react";
import { api, fileUrl, formatDate } from "@/lib/api";
import { Reveal, Empty } from "@/components/kit";
import { STRUCTURE_LABEL } from "@/lib/content";

export function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(path).then((r) => alive && setData(r.data)).catch(() => alive && setData([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}

const initials = (n) => n.replace(/^(Mme|M\.|Mlle)\s+/, "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("");

export function MemberGrid({ members }) {
  if (!members) return <p className="text-neutral-500">Chargement…</p>;
  if (!members.length) return <Empty testid="members-empty">[Texte à compléter] Aucun membre pour le moment.</Empty>;
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="member-grid">
      {members.map((m, i) => (
        <Reveal key={m.id} delay={Math.min(i * 0.04, 0.3)}>
          <article className="card group h-full overflow-hidden" data-testid={`member-card-${i}`}>
            <div className="relative aspect-[4/5] overflow-hidden bg-u-soft">
              {m.photo_url ? (
                <img src={fileUrl(m.photo_url)} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center pagne">
                  <span className="font-display text-5xl font-bold text-u" aria-hidden="true">{initials(m.name)}</span>
                  <User className="mt-2 h-5 w-5 text-u/60" aria-hidden="true" />
                  <span className="sr-only">Photo à venir</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 h-1 w-full bg-u" />
            </div>
            <div className="p-5">
              <p className="font-display text-sm font-semibold uppercase tracking-wider text-u">{m.role}</p>
              <h3 className="mt-1 font-display text-xl font-bold uppercase leading-tight text-ink">{m.name}</h3>
              {m.description && <p className="mt-2 text-sm text-neutral-600">{m.description}</p>}
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  );
}

export function ArticleCard({ a, i = 0 }) {
  const color = a.category === "association" ? "bg-asso" : a.category === "ong" ? "bg-ong" : "bg-gold text-ink";
  return (
    <Reveal delay={Math.min(i * 0.06, 0.3)}>
      <Link to={`/actualites/${a.slug}`} className="card group flex h-full flex-col overflow-hidden" data-testid={`article-card-${a.slug}`}>
        <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
          {a.cover_url ? (
            <img src={fileUrl(a.cover_url)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full items-center justify-center bg-ink pagne-light">
              <span className="font-display text-3xl font-bold uppercase text-gold">Ma Leba</span>
            </div>
          )}
          <span className={`absolute left-3 top-3 rounded-full px-3 py-1 font-display text-xs font-semibold uppercase tracking-wider text-white ${color}`}>
            {STRUCTURE_LABEL[a.category] || "Commun"}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <p className="text-xs text-neutral-500">{formatDate(a.created_at)}</p>
          <h3 className="mt-1 font-display text-xl font-bold uppercase leading-tight text-ink group-hover:text-u">{a.title}</h3>
          <p className="mt-2 line-clamp-3 flex-1 text-neutral-600">{a.excerpt}</p>
          <span className="mt-4 inline-flex items-center gap-1 font-display text-sm font-semibold uppercase text-u">
            Lire <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}
